import { useRegisterSeg } from "../lib/segRegistry";
import React from "react";
import { useShiftHold } from "../lib/useShiftHold";
import { useAutoPin } from "../lib/useAutoPin";

// Shared pill/segmented button group used across stage/season/stat toggles.
// options: [{ k, l, gold?, mono?, testId? }]
// Optional pinning: pass pinnedKey (the currently-pinned value) + onPin(k) to
// enable shift+click "pin as default" (a gold pin shows on the pinned option;
// holding Shift for 1s dims the non-pinned options).
export function SegmentedControl({ value, onChange, options, testId, size = "sm", className = "", accent = "", pinnedKey: pinProp = null, onPin: onPinProp = null, pinReady = true, noPin = false, variant = "" }) {
    const nhl = variant === "nhl";  // NHL nav-bar look (matches the NHL page picker): grey text, white active, divided
    const { shift } = useShiftHold();
    const auto = useAutoPin(onPinProp || noPin ? null : testId, value, onChange, options.map((o) => o.k), pinReady);
    const onPin = onPinProp || auto.onPin;
    const pinnedKey = onPinProp ? pinProp : auto.pinnedKey;
    useRegisterSeg({ keys: options.map((o) => o.k), value, onPin: noPin ? null : onPin, pinnedKey });
    const pad = size === "lg" ? "px-3.5 sm:px-4 py-1.5 sm:py-2 text-[11px] sm:text-xs" : "px-2.5 py-1.5 text-[11px]";
    return (
        <div className={`inline-flex rounded-lg border border-white/15 overflow-hidden bg-black/25 ${className}`} data-testid={testId}>
            {options.map((o, oi) => {
                const active = value === o.k;
                const activeAccent = o.accent || accent;
                let cls, style;
                if (active) {
                    if (o.gold) cls = "bg-[#F6C453] text-black";
                    else if (activeAccent) { cls = "font-bold"; style = { color: activeAccent, backgroundColor: `${activeAccent}26` }; }
                    else if (nhl) { cls = "font-bold text-white bg-[#8A94A6]/20"; }
                    // Standard active = low-opacity purple fill (matches team-nav pill opacity), not full-opacity.
                    else { cls = "font-bold"; style = { color: "#D9BEF5", backgroundColor: "rgba(157,76,221,0.22)" }; }
                } else if (activeAccent) { cls = "hover:brightness-110"; style = { color: activeAccent }; }
                else cls = nhl ? "text-[#C3CAD6] hover:text-white" : "text-[#C9A6F0] hover:text-white";
                if (nhl && oi > 0) cls += " border-l border-white/10";
                return (
                    <button
                        key={o.k}
                        type="button"
                        onClick={(e) => { if ((e.shiftKey || shift) && onPin) onPin(o.k); onChange(o.k); }}
                        data-testid={o.testId || (testId ? `${testId}-${o.k}` : undefined)}
                        title={onPin ? "Click to select · Shift+click to pin as default (click the pinned one again with Shift to unpin)" : undefined}
                        style={style}
                        className={`relative ${pad} font-bold uppercase tracking-wider transition-colors ${o.mono ? "font-mono-stat" : ""} ${cls} ${onPin && shift && o.k !== (pinnedKey || value) ? "opacity-50" : ""}`}
                    >
                        {o.l}
                        {onPin && pinnedKey === o.k && <span className="absolute top-0.5 right-0.5 h-1.5 w-1.5 rounded-full bg-[#F5B301]" data-testid={`${testId}-pin-${o.k}`} />}
                    </button>
                );
            })}
        </div>
    );
}

export default SegmentedControl;
